package games.polyclub.website.frontend.store.context

import games.polyclub.frontend.lib.messagerenderer.Viewer
import games.polyclub.frontend.lib.react.simplified.SFC
import games.polyclub.message.BuildString
import games.polyclub.message.Receiver
import games.polyclub.message.div
import games.polyclub.message.unaryMinus
import games.polyclub.service.payment.api.id.OfferId
import games.polyclub.service.player.id.api.PlayerId
import games.polyclub.service.player.id.api.SystemPlayerId
import games.polyclub.service.store.api.CartProduct
import games.polyclub.service.store.api.NameAndId
import games.polyclub.service.store.api.ShoppingCart
import games.polyclub.tool.endpoint.GMson
import games.polyclub.tool.functional.Option
import games.polyclub.tool.strings.Currency
import games.polyclub.website.frontend.store.context.CartAddData.Who.*
import games.polyclub.website.frontend.store.hooks.useAuth
import jquery.jQuery
import js.core.jso
import kotlinx.collections.immutable.PersistentList
import kotlinx.collections.immutable.toPersistentList
import kotlinx.serialization.InternalSerializationApi
import kotlinx.serialization.encodeToString
import org.intellij.lang.annotations.Language
import react.*
import web.dom.document
import web.events.Event
import web.events.EventType
import web.html.HTMLAnchorElement
import web.html.HTMLDivElement
import web.prompts.alert
import web.storage.localStorage
import web.timers.Timeout
import web.timers.clearTimeout
import web.timers.setTimeout
import kotlin.js.json
import kotlin.time.Duration.Companion.seconds

external interface CartContextData {
    var modalRef: MutableRefObject<HTMLDivElement>
    var cartButtonRef: MutableRefObject<HTMLAnchorElement>
    var addToCart: (CartAddData, Boolean) -> Boolean
    var showModal: () -> Unit
    var hideModal: (Boolean) -> Unit
    var cart: ShoppingCart
    var selectedProducts: PersistentList<CartProduct>
    var setProductList: (PersistentList<CartProduct>) -> Unit
    var getCurrency: () -> Currency
    var selfIdentification: NameAndId?
    var setSelfIdentification: (NameAndId?) -> Unit
}

data class CartAddData(
    val toWho: Who,
    val to: Option<PlayerId>?,
    val offerId: OfferId,
    val quantity: Int = 1,
) {
    enum class Who {
        ME, GIFT, GIVEAWAY
    }
}

val CartContext = createContext<CartContextData>()

val CartProvider = SFC<PropsWithChildren> { props ->
    val authCtx = useAuth()

    val (persistence, setPersistence) = useState(try {
        localStorage.getItem("cart")?.let {
            GMson.decodeFromString<ShoppingCart>(it)
        } ?: ShoppingCart()
    } catch (e: Exception) { e.printStackTrace(); ShoppingCart() })

    val savePersistence = useCallback(setPersistence) { modifier: (ShoppingCart) -> ShoppingCart ->
        setPersistence { current ->
            console.log("savePersistence.current", current.toString())
            val updated = modifier(current)
            console.log("savePersistence.updated", updated.toString())
            try {
                localStorage.setItem("cart", GMson.encodeToString(updated))
            } catch (e: Throwable) {
                e.printStackTrace()
            }
            updated
        }
    }

    val setSelfIdentification = useCallback(savePersistence) { selfId: NameAndId? ->
        savePersistence { current ->
            val newPersistence = current.copy(selfId = selfId)
            console.log("setSelfIdentification", newPersistence.toString())
            console.log("before", current.toString())
            newPersistence
        }
    }

    val setProductList = useCallback(savePersistence) { products: List<CartProduct> ->
        savePersistence { current ->
            val newPersistence = current.copy(offers = products.filter { it.quantity > 0 }.toPersistentList())
            console.log("setProductList", newPersistence.toString())
            console.log("before", current.toString())
            console.log("products", products.toString())
            newPersistence
        }
    }

    val getCurrency = useCallback(persistence) {
        persistence.currency
    }

    val setCurrency = useCallback(savePersistence) { currency: Currency ->
        savePersistence { current ->
            val newPersistence = current.copy(currency = currency)
            console.log("setCurrency", newPersistence.toString())
            console.log("before", current.toString())
            newPersistence
        }
    }

    useEffect(authCtx.auth?.currency) {
        val newCurrency = authCtx.auth?.currency
        if (newCurrency != null && newCurrency != getCurrency()) {
            setCurrency(newCurrency)
        }
    }

    val modalRef = useRef<HTMLDivElement>()
    val cartButtonRef = useRef<HTMLAnchorElement>()

    val showModal = useCallback(modalRef) {
        jQuery(modalRef.current).asDynamic().modal("show")
        Unit
    }

    val (showPopOver, setShowPopOver) = useState(true)
    val getShowPopOver = useCallback(showPopOver) {
        showPopOver
    }

    val showPopOverAction = useCallback(cartButtonRef, cartButtonRef.current) { show: Boolean ->
        jQuery(cartButtonRef.current!!).asDynamic().popover(
            if (show) "show" else "hide"
        )
        Unit
    }

    useEffect(getShowPopOver, setShowPopOver, modalRef, modalRef.current) {
        var invalidated = false
        cleanup { invalidated = true }
        modalRef.current?.let { modal ->
            jQuery(modal).on("hidden.bs.modal") { _ ->
                if (!invalidated) {
                    if (getShowPopOver()) {
                        showPopOverAction(true)
                        var timeout: Timeout? = null
                        fun handleClick(@Suppress("UNUSED_PARAMETER") event: Event?) {
                            showPopOverAction(false)
                            timeout?.let { clearTimeout(it) }
                            document.removeEventListener(EventType("click"), ::handleClick)
                        }
                        timeout = setTimeout(30.seconds) {
                            handleClick(null)
                        }
                        document.addEventListener(EventType("click"), ::handleClick)
                    } else {
                        setShowPopOver(true)
                    }
                }
            }
        }
    }

    val hideModal = useCallback(modalRef, setShowPopOver) { show: Boolean ->
        setShowPopOver(show)
        jQuery(modalRef.current).asDynamic().modal("hide")
    }

    useEffect(cartButtonRef, cartButtonRef.current) {
        cartButtonRef.current?.let { cartButton ->
            @OptIn(InternalSerializationApi::class)
            @Language("HTML")
            val template = """
                <div class="popover" role="tooltip"><div class="arrow"></div><h3 class="popover-header bg-primary"></h3><div class="popover-body"></div></div>
            """.trimIndent()
            jQuery(cartButton).asDynamic()
                .popover(
                    json(
                        "title" to "Carrinho de Compras",
                        "content" to (-"Clique aqui quando estiver ${"pronta"/ Receiver} para finalizar sua compra." - BuildString(
                            Viewer
                        )),
                        "trigger" to "manual",
                        "template" to template
                    )
                )
        }
        Unit
    }

    val addToCart = useCallback<(CartAddData, Boolean) -> Boolean>(showModal, persistence, persistence.offers, setProductList) { data, show ->
        if (data.toWho == GIFT && data.to?.isPresent != true) {
            alert("Por favor digite um apelido válido.")
            return@useCallback false
        }
        val gift = when (data.toWho) {
            ME -> null
            GIFT -> data.to?.getOrNull()
            GIVEAWAY -> SystemPlayerId.GIVEAWAY
        }
        val orderProductIndex = when (data.toWho) {
            ME -> persistence.offers.indexOfFirst { product ->
                product.giftTo == null && product.offerId == data.offerId
            }.takeIf { it >= 0 }
            GIFT, GIVEAWAY -> persistence.offers.indexOfFirst { product ->
                product.giftTo == gift && product.offerId == data.offerId
            }.takeIf { it >= 0 }
        }

        orderProductIndex?.let { index ->
            val existing = persistence.offers[index]
            val toAdd = existing.copy(quantity = existing.quantity + data.quantity)
            console.log("existing", toAdd.toString())
            setProductList(persistence.offers.set(index, toAdd))
            if (show) {
                showModal()
            }
            return@useCallback true
        }

        val toAdd = CartProduct(
            offerId = data.offerId,
            giftTo = gift,
            quantity = data.quantity,
        )
        console.log("toAdd", toAdd.toString())
        setProductList(persistence.offers.add(
            toAdd
        ))
        if (show) {
            showModal()
        }
        true
    }

    CartContext.Provider {
        value = jso {
            this.setProductList = setProductList
            this.selectedProducts = persistence.offers
            this.getCurrency = getCurrency
            this.showModal = showModal
            this.hideModal = hideModal
            this.addToCart = addToCart
            this.modalRef = modalRef
            this.cartButtonRef = cartButtonRef
            this.selfIdentification = persistence.selfId
            this.setSelfIdentification = setSelfIdentification
            this.cart = persistence
        }
        +props.children
    }
}

fun useCart() = checkNotNull(useContext(CartContext)) {
    "CartContext indisponível"
}
